import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, Circle, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import { Badge } from '../ui/badge';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const markerColors = {
  road_closure: '#EF4444',
  detour: '#F97316',
  parade_route: '#3B82F6',
  closed_area: '#EF4444',
};

const MapTypeLabels = {
  road_closure: 'Road Closure',
  detour: 'Detour',
  parade_route: 'Parade Route',
  closed_area: 'Closed Area',
};

// Fetch road-following route from OSRM
const fetchRoute = async (coordinates) => {
  if (!coordinates || coordinates.length < 2) return null;
  
  try {
    // Build OSRM URL with all waypoints
    const coordString = coordinates
      .map(c => `${c.lng},${c.lat}`)
      .join(';');
    
    const url = `https://router.project-osrm.org/route/v1/driving/${coordString}?overview=full&geometries=geojson`;
    
    const response = await fetch(url);
    const data = await response.json();
    
    if (data.code === 'Ok' && data.routes && data.routes[0]) {
      // Return the full route geometry
      return data.routes[0].geometry.coordinates.map(coord => ({
        lat: coord[1],
        lng: coord[0]
      }));
    }
  } catch (error) {
    console.error('Error fetching route:', error);
  }
  
  return null;
};

// Custom marker icons using SVG
const createCustomIcon = (color, type) => {
  let iconSvg = '';
  
  switch (type) {
    case 'road_closure':
      iconSvg = `<circle cx="12" cy="12" r="8" fill="${color}"/><line x1="8" y1="8" x2="16" y2="16" stroke="white" stroke-width="2"/><line x1="16" y1="8" x2="8" y2="16" stroke="white" stroke-width="2"/>`;
      break;
    case 'detour':
      iconSvg = `<circle cx="12" cy="12" r="8" fill="${color}"/><path d="M12 6 L16 12 L12 18 L8 12 Z" fill="white"/>`;
      break;
    case 'parade_route':
      iconSvg = `<circle cx="12" cy="12" r="8" fill="${color}"/><polygon points="12,6 14,10 18,10 15,13 16,17 12,15 8,17 9,13 6,10 10,10" fill="white"/>`;
      break;
    case 'closed_area':
      iconSvg = `<circle cx="12" cy="12" r="8" fill="${color}"/><path d="M12 7 L12 13 M12 15 L12 17" stroke="white" stroke-width="2" stroke-linecap="round"/>`;
      break;
    default:
      iconSvg = `<circle cx="12" cy="12" r="8" fill="${color}"/>`;
  }
  
  return L.divIcon({
    className: 'custom-marker-icon',
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background: white;
        border: 3px solid ${color};
        border-radius: 50%;
        box-shadow: 0 3px 10px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg width="24" height="24" viewBox="0 0 24 24">
          ${iconSvg}
        </svg>
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
};

// Line endpoint marker
const createEndpointIcon = (color, isStart) => {
  return L.divIcon({
    className: 'endpoint-marker',
    html: `
      <div style="
        width: 16px;
        height: 16px;
        background: ${color};
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      ">
        ${isStart ? '<div style="position:absolute;top:-20px;left:50%;transform:translateX(-50%);font-size:10px;font-weight:bold;color:white;background:'+color+';padding:1px 4px;border-radius:3px;white-space:nowrap;">START</div>' : ''}
      </div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
  });
};

// Component to handle map resize
const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const handleResize = () => {
      map.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    setTimeout(handleResize, 100);
    return () => window.removeEventListener('resize', handleResize);
  }, [map]);
  return null;
};

// Fit the map to show all item coordinates (used for print/export view)
const FitBounds = ({ items }) => {
  const map = useMap();
  useEffect(() => {
    const allPoints = [];
    items.forEach((item) => {
      (item.coordinates || []).forEach((c) => allPoints.push([c.lat, c.lng]));
    });
    if (allPoints.length === 0) return;
    const timer = setTimeout(() => {
      try {
        if (allPoints.length === 1) {
          map.setView(allPoints[0], 15, { animate: false });
        } else {
          map.fitBounds(allPoints, { padding: [40, 40], maxZoom: 16, animate: false });
        }
      } catch (e) {
        // ignore fit errors
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [items, map]);
  return null;
};

// Component to render a route that follows roads
const RoutedPolyline = ({ item, color, style, onItemClick }) => {
  const [routeCoords, setRouteCoords] = useState(null);
  const [loading, setLoading] = useState(true);
  const coords = item.coordinates;
  const positions = coords.map(c => [c.lat, c.lng]);

  useEffect(() => {
    let isMounted = true;
    
    const loadRoute = async () => {
      if (coords.length < 2) {
        setLoading(false);
        return;
      }
      
      const route = await fetchRoute(coords);
      if (isMounted) {
        if (route) {
          setRouteCoords(route.map(c => [c.lat, c.lng]));
        }
        setLoading(false);
      }
    };
    
    loadRoute();
    
    return () => {
      isMounted = false;
    };
  }, [coords]);

  // Use routed coordinates if available, otherwise fall back to original
  const displayPositions = routeCoords || positions;

  return (
    <React.Fragment>
      {/* Main line with outline for visibility */}
      <Polyline
        positions={displayPositions}
        pathOptions={{
          color: 'white',
          weight: style.weight + 4,
          opacity: 0.5,
        }}
      />
      {/* Actual colored line */}
      <Polyline
        positions={displayPositions}
        pathOptions={{
          ...style,
          // Show dashed line while loading
          dashArray: loading ? '5, 10' : style.dashArray,
        }}
        eventHandlers={{
          click: () => onItemClick(item),
        }}
      >
        <Popup>
          <div className="p-2 min-w-[220px]">
            <Badge 
              className="mb-2"
              style={{ backgroundColor: color, color: 'white' }}
            >
              {MapTypeLabels[item.item_type]}
            </Badge>
            <h3 className="font-bold text-gray-900 text-base">{item.title}</h3>
            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
            <p className="text-xs text-gray-500 mt-2">
              {positions.length} waypoints • {routeCoords ? 'Following roads' : 'Direct path'}
            </p>
          </div>
        </Popup>
      </Polyline>
      
      {/* Start marker */}
      <Marker
        position={positions[0]}
        icon={createEndpointIcon(color, true)}
      >
        <Popup>
          <div className="p-1 text-sm">
            <strong>Start:</strong> {item.title}
          </div>
        </Popup>
      </Marker>
      
      {/* End marker */}
      <Marker
        position={positions[positions.length - 1]}
        icon={createEndpointIcon(color, false)}
      >
        <Popup>
          <div className="p-1 text-sm">
            <strong>End:</strong> {item.title}
          </div>
        </Popup>
      </Marker>
      
      {/* Intermediate waypoints (original coordinates, not routed) */}
      {positions.slice(1, -1).map((pos, idx) => (
        <CircleMarker
          key={`waypoint-${idx}`}
          center={pos}
          radius={4}
          pathOptions={{
            color: color,
            fillColor: 'white',
            fillOpacity: 1,
            weight: 2,
          }}
        />
      ))}
    </React.Fragment>
  );
};

const EmergencyMap = ({ 
  items = [], 
  center = [46.1268, -112.9536], // Anaconda, MT coordinates
  zoom = 13,
  height = '100%',
  showLegend = true,
  onItemClick,
  interactive = true,
  fitToItems = false,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);

  const handleItemClick = (item) => {
    setSelectedItem(item);
    if (onItemClick) {
      onItemClick(item);
    }
  };

  const renderMapItem = (item) => {
    const color = markerColors[item.item_type] || '#6B7280';
    const coords = item.coordinates;

    if (!coords || coords.length === 0) return null;

    // Single point - render as marker (except circular closed areas, handled below)
    const isCircleArea = item.item_type === 'closed_area' && item.radius && item.radius > 0;
    if (coords.length === 1 && !isCircleArea) {
      return (
        <Marker
          key={item.id}
          position={[coords[0].lat, coords[0].lng]}
          icon={createCustomIcon(color, item.item_type)}
          eventHandlers={{
            click: () => handleItemClick(item),
          }}
        >
          <Popup>
            <div className="p-2 min-w-[220px]">
              <Badge 
                className="mb-2"
                style={{ backgroundColor: color, color: 'white' }}
              >
                {MapTypeLabels[item.item_type]}
              </Badge>
              <h3 className="font-bold text-gray-900 text-base">{item.title}</h3>
              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              {item.is_active && (
                <div className="mt-2 flex items-center text-xs text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                  Active
                </div>
              )}
            </div>
          </Popup>
        </Marker>
      );
    }

    // Multiple points - render as line or polygon
    const positions = coords.map(c => [c.lat, c.lng]);

    // Closed Area - render as circle (radius) or polygon (corners)
    if (item.item_type === 'closed_area') {
      // Circular closed area: single center point + radius (meters)
      if (item.radius && item.radius > 0) {
        return (
          <React.Fragment key={item.id}>
            <Circle
              center={positions[0]}
              radius={item.radius}
              pathOptions={{
                color: color,
                fillColor: color,
                fillOpacity: 0.25,
                weight: 3,
                dashArray: '10, 5',
              }}
              eventHandlers={{
                click: () => handleItemClick(item),
              }}
            >
              <Popup>
                <div className="p-2 min-w-[220px]">
                  <Badge
                    className="mb-2"
                    style={{ backgroundColor: color, color: 'white' }}
                  >
                    {MapTypeLabels[item.item_type]}
                  </Badge>
                  <h3 className="font-bold text-gray-900 text-base">{item.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Radius: {item.radius >= 1000 ? `${(item.radius / 1000).toFixed(2)} km` : `${Math.round(item.radius)} m`}
                  </p>
                </div>
              </Popup>
            </Circle>
            {/* Center marker */}
            <CircleMarker
              center={positions[0]}
              radius={5}
              pathOptions={{
                color: color,
                fillColor: 'white',
                fillOpacity: 1,
                weight: 2,
              }}
            />
          </React.Fragment>
        );
      }

      // Legacy polygon closed area (3+ corner points)
      return (
        <React.Fragment key={item.id}>
          <Polygon
            positions={positions}
            pathOptions={{
              color: color,
              fillColor: color,
              fillOpacity: 0.25,
              weight: 3,
              dashArray: '10, 5',
            }}
            eventHandlers={{
              click: () => handleItemClick(item),
            }}
          >
            <Popup>
              <div className="p-2 min-w-[220px]">
                <Badge 
                  className="mb-2"
                  style={{ backgroundColor: color, color: 'white' }}
                >
                  {MapTypeLabels[item.item_type]}
                </Badge>
                <h3 className="font-bold text-gray-900 text-base">{item.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{item.description}</p>
              </div>
            </Popup>
          </Polygon>
          {/* Corner markers */}
          {positions.map((pos, idx) => (
            <CircleMarker
              key={`${item.id}-corner-${idx}`}
              center={pos}
              radius={5}
              pathOptions={{
                color: color,
                fillColor: 'white',
                fillOpacity: 1,
                weight: 2,
              }}
            />
          ))}
        </React.Fragment>
      );
    }

    // Road Closure, Detour, Parade Route - render as polyline with proper styling
    const lineStyle = {
      road_closure: {
        color: color,
        weight: 6,
        opacity: 0.9,
        dashArray: '15, 10',
        lineCap: 'round',
      },
      detour: {
        color: color,
        weight: 5,
        opacity: 0.9,
        dashArray: '1, 10',
        lineCap: 'round',
      },
      parade_route: {
        color: color,
        weight: 5,
        opacity: 0.9,
        dashArray: null,
        lineCap: 'round',
      },
    };

    const style = lineStyle[item.item_type] || { color, weight: 4, opacity: 0.8 };

    // Use RoutedPolyline component for road-following routes
    return (
      <RoutedPolyline
        key={item.id}
        item={item}
        color={color}
        style={style}
        onItemClick={handleItemClick}
      />
    );
  };

  return (
    <div className="relative w-full" style={{ height }} data-testid="emergency-map">
      <MapContainer
        center={center}
        zoom={zoom}
        className="w-full h-full rounded-lg"
        zoomControl={interactive}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapResizer />
        {fitToItems && <FitBounds items={items} />}
        {items.map(renderMapItem)}
      </MapContainer>

      {/* Legend */}
      {showLegend && (
        <div className="absolute bottom-4 left-4 z-[1000] glass-panel rounded-lg p-4" data-testid="map-legend">
          <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">Legend</h4>
          <div className="space-y-2">
            {Object.entries(MapTypeLabels).map(([type, label]) => (
              <div key={type} className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <div
                    className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                    style={{ backgroundColor: markerColors[type] }}
                  />
                  {type !== 'closed_area' && (
                    <div 
                      className="w-8 h-1 rounded"
                      style={{ 
                        backgroundColor: markerColors[type],
                        opacity: 0.8,
                      }}
                    />
                  )}
                </div>
                <span className="text-xs text-zinc-300">{label}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-zinc-700">
            <p className="text-[10px] text-zinc-500">
              Click on any marker or line for details
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyMap;
